import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Home } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

interface BreadcrumbItem {
  label: string;
  labelAr?: string;
  href?: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
  className?: string;
}

// Route to breadcrumb mapping
const routeLabels: Record<string, { en: string; ar: string }> = {
  "/": { en: "Home", ar: "الرئيسية" },
  "/imported-events": { en: "Events", ar: "الفعاليات" },
  "/social-groups": { en: "Social Groups", ar: "مجموعات التواصل" },
  "/x-spaces": { en: "X Spaces", ar: "مساحات X" },
  "/exhibitions": { en: "Exhibitions", ar: "المعارض" },
  "/conferences-seminars": { en: "Conferences & Seminars", ar: "المؤتمرات والندوات" },
  "/saudi-seasons": { en: "Saudi Seasons", ar: "المواسم السعودية" },
  "/saudi-festivals": { en: "Saudi Festivals", ar: "المهرجانات السعودية" },
  "/public-occasions": { en: "Public Occasions", ar: "المناسبات العامة" },
  "/international-days": { en: "International Days", ar: "الأيام الدولية" },
  "/applications": { en: "Applications", ar: "التطبيقات" },
  "/packages": { en: "Packages", ar: "الباقات" },
  "/submit": { en: "Submit Event", ar: "إضافة فعالية" },
  "/my-submissions": { en: "My Submissions", ar: "طلباتي" },
  "/profile": { en: "Profile", ar: "الملف الشخصي" },
  "/auth": { en: "Login", ar: "تسجيل الدخول" },
};

export const Breadcrumb = ({ items, className = "" }: BreadcrumbProps) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const isRTL = i18n.language === "ar";

  // Generate breadcrumbs from current path if not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (items) return items;

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: "Home", labelAr: "الرئيسية", href: "/" },
    ];

    let currentPath = "";
    pathSegments.forEach((segment) => {
      currentPath += `/${segment}`;
      const routeLabel = routeLabels[currentPath];
      
      if (routeLabel) {
        breadcrumbs.push({
          label: routeLabel.en,
          labelAr: routeLabel.ar,
          href: currentPath,
        });
      } else if (segment !== "event" && segment !== "category") {
        // Handle dynamic segments like event IDs
        breadcrumbs.push({
          label: decodeURIComponent(segment),
          labelAr: decodeURIComponent(segment),
        });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbItems = generateBreadcrumbs();
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  // Generate JSON-LD structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": isRTL ? (item.labelAr || item.label) : item.label,
      "item": item.href ? `https://visitevent.lovable.app${item.href}` : undefined,
    })),
  };

  // Don't show breadcrumb on homepage
  if (location.pathname === "/") {
    return null;
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <nav
        aria-label="Breadcrumb"
        className={`flex items-center gap-1 text-sm text-muted-foreground py-3 ${className}`}
      >
        <ol className="flex items-center gap-1 flex-wrap">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            const displayLabel = isRTL ? (item.labelAr || item.label) : item.label;

            return (
              <li key={index} className="flex items-center gap-1">
                {index > 0 && (
                  <ChevronIcon className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
                )}
                
                {isLast || !item.href ? (
                  <span
                    className={`${
                      isLast
                        ? "text-foreground font-medium"
                        : "text-muted-foreground"
                    } ${index === 0 ? "flex items-center gap-1" : ""}`}
                    aria-current={isLast ? "page" : undefined}
                  >
                    {index === 0 && <Home className="w-4 h-4" />}
                    <span className="hidden sm:inline">{displayLabel}</span>
                  </span>
                ) : (
                  <Link
                    to={item.href}
                    className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                  >
                    {index === 0 && <Home className="w-4 h-4" />}
                    <span className="hidden sm:inline">{displayLabel}</span>
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

// Specialized breadcrumb for event details page
interface EventBreadcrumbProps {
  eventTitle: string;
  eventTitleAr?: string;
  category?: string;
  categoryLabel?: string;
  categoryLabelAr?: string;
}

export const EventBreadcrumb = ({
  eventTitle,
  eventTitleAr,
  category,
  categoryLabel,
  categoryLabelAr,
}: EventBreadcrumbProps) => {
  const items: BreadcrumbItem[] = [
    { label: "Home", labelAr: "الرئيسية", href: "/" },
    { label: "Events", labelAr: "الفعاليات", href: "/imported-events" },
  ];

  if (category && categoryLabel) {
    items.push({
      label: categoryLabel,
      labelAr: categoryLabelAr || categoryLabel,
      href: `/${category}`,
    });
  }

  items.push({
    label: eventTitle.length > 40 ? eventTitle.substring(0, 40) + "..." : eventTitle,
    labelAr: eventTitleAr 
      ? (eventTitleAr.length > 40 ? eventTitleAr.substring(0, 40) + "..." : eventTitleAr)
      : undefined,
  });

  return <Breadcrumb items={items} />;
};
